import { TradingAccountStatusesEnum } from '../models/crm/types';

import { NullableString } from './types';

/** Statuses for which a trading account exists but is not yet usable — the account is still being
 *  provisioned, so the trade connect gate keeps polling instead of connecting. */
export const PENDING_TRADING_ACCOUNT_STATUSES: readonly TradingAccountStatusesEnum[] = [
  TradingAccountStatusesEnum.ForApproval,
  TradingAccountStatusesEnum.PendingCreation,
];

/** Statuses for which a trading account is permanently unusable. */
export const CLOSED_TRADING_ACCOUNT_STATUSES: readonly TradingAccountStatusesEnum[] = [
  TradingAccountStatusesEnum.Closed,
];

/** The account exists but is still being created - every account scoped call answers 503 for it. */
export const isPendingAccountStatus = (status: TradingAccountStatusesEnum): boolean =>
  PENDING_TRADING_ACCOUNT_STATUSES.includes(status);

/** The account is permanently unusable. Its data is still readable, it just cannot be traded on. */
export const isClosedAccountStatus = (status: TradingAccountStatusesEnum): boolean =>
  CLOSED_TRADING_ACCOUNT_STATUSES.includes(status);

export const USABLE_TRADING_ACCOUNT_STATUSES: readonly TradingAccountStatusesEnum[] = [
  TradingAccountStatusesEnum.Active,
  TradingAccountStatusesEnum.ClosedCancel,
];

/** A trading account can be worked with. Blocked, restricted, rejected and pending ones cannot. */
export const isUsableAccountStatus = (status: TradingAccountStatusesEnum): boolean =>
  USABLE_TRADING_ACCOUNT_STATUSES.includes(status);

/**
 * The account finished being created.
 *
 * Matched as a *pair*, not on the new status, because `ClosedCancel` is the out of hours status of
 * every account: `Active -> ClosedCancel` and back arrive twice a day and mean nothing about the
 * account's lifecycle. Acting on the new status alone would rebuild every connection each time.
 *
 * `ForApproval -> Active` is deliberately not included. That wait is measured in days, the
 * application will have been restarted, and the cold start path picks the account up. There is no
 * event for it yet; when there is, this is where it goes.
 */
export const hasBecomeUsable = (
  previousStatus: TradingAccountStatusesEnum,
  status: TradingAccountStatusesEnum,
): boolean => previousStatus === TradingAccountStatusesEnum.PendingCreation
  && status === TradingAccountStatusesEnum.Active;

/**
 * The account is finished - the plan either failed or passed, and both end it the same way. Which
 * of the two it was comes from the closeout event, or from the accounts summary when none arrived.
 */
export const hasBecomeFinished = (
  previousStatus: TradingAccountStatusesEnum,
  status: TradingAccountStatusesEnum,
): boolean => isUsableAccountStatus(previousStatus)
  && status === TradingAccountStatusesEnum.Closed;

export const DEMO_ACCOUNT_REFERENCE_PREFIXES: readonly string[] = [ 'DEM', 'DES' ];

export const isDemoAccountReference = (referenceId?: string|null): boolean => (
  DEMO_ACCOUNT_REFERENCE_PREFIXES.some(prefix => (referenceId ?? '').includes(prefix))
);

/** It can't be more than one demo account */
export const getDemoTradingAccount = (tradingAccounts: string[]): NullableString =>
  tradingAccounts.find(isDemoAccountReference) ?? null;

export const DEMO_ACCOUNT_ACTIVATION_KEY = 'activatingDemoAccount';

export const markDemoAccountActivating = (): void => sessionStorage.setItem(DEMO_ACCOUNT_ACTIVATION_KEY, 'true');

export const clearDemoAccountActivating = (): void => sessionStorage.removeItem(DEMO_ACCOUNT_ACTIVATION_KEY);

/** True between the demo activation request and the moment the account is returned as usable. */
export const isDemoAccountActivating = (): boolean => !!sessionStorage.getItem(DEMO_ACCOUNT_ACTIVATION_KEY);
